<script setup lang="ts">
import {
  ref,
  computed,
} from 'vue';

import { ChevronDownIcon, ChevronUpIcon, MagnifierIcon } from '@thehotelsnetwork/icons/vue/linear';
import { useSearchList } from '@thehotelsnetwork/usables';
import { useRouter } from 'vue-router';

import { useAccountsService } from '../../services/AccountsService';
import type { Account } from '../../types';

import { useErrors } from '@/composition/useErrors';
import Truncate from '@/components/Truncate/Truncate.vue';
import { clearGlobalPropertyInfoCache } from '@/modules/Pages/composables/usePagesProperties';
import {
  getQueryClient,
  invalidateAccountScopedQueries,
} from '@/modules/QueryClient';

const router = useRouter();
const accountsService = useAccountsService();
const { showResponseErrors } = useErrors();

const accountFilter = ref('');
const isSwitchingAccount = ref(false);
const currentAccount = computed(() => accountsService.account);

const isFetching = computed(() => accountsService.isFetching);
const userHasMultipleAccounts = computed(() => accountsService.accounts.length > 1);

const accounts = useSearchList({
  searchTarget: accountFilter,
  searchKeys: ['name'],
})(accountsService.accounts);

const accountName = computed(() => (currentAccount.value?.name || '').split(' '));
const accountLogo = computed(() => currentAccount.value?.logo || null);

async function handleAccountSelected(account: Account, closeMenu: Function) {
  isSwitchingAccount.value = true;
  closeMenu();
  try {
    await accountsService.selectUserAccount(account);
    clearGlobalPropertyInfoCache();
    await invalidateAccountScopedQueries(getQueryClient(), account.id);
    await router.push('/properties');
    isSwitchingAccount.value = false;
  } catch (error) {
    isSwitchingAccount.value = false;
    showResponseErrors(error);
  }
}
</script>
<template>
  <LpDropdown
    :disabled="!userHasMultipleAccounts || isFetching || isSwitchingAccount"
    data-testid="AccountSwitch"
  >
    <template #btn="{ isOpen }">
      <div
        :class="[
          'flex select-none cursor-pointer items-center py-2 px-3 space-x-3',
          userHasMultipleAccounts && 'border rounded-lg border-base-400 py-1 bg-base-100',
          isFetching && 'opacity-75',
        ]"
      >
        <div class="flex overflow-hidden justify-center items-center w-8 h-8 rounded-full">
          <LpAvatar
            v-if="accountLogo === null"
            :first-name="accountName[0] || ''"
            :last-name="accountName[1] || ''"
            class="flex-shrink-0"
          />
          <img
            v-else
            :src="`${accountLogo}?t=${Date.now()}`"
            class="max-h-full"
            alt=""
          >
        </div>
        <div class="flex-1 min-w-0 text-base-900">
          <div class="flex items-end uppercase text-xxs text-base-700">
            {{ $t('Account') }}
          </div>
          <Truncate
            v-slot="{ isTruncate, isMouseOver }"
            class="w-full text-lg capitalize"
          >
            {{ currentAccount?.name }}
            <div
              v-if="isTruncate && isMouseOver"
              class="absolute p-2 bg-white rounded shadow-sm -translate-y-3/4"
            >
              {{ currentAccount?.name }}
            </div>
          </Truncate>
        </div>
        <div v-if="userHasMultipleAccounts">
          <LpLoader
            v-if="isFetching && !isSwitchingAccount"
            ring-size="20"
            class="text-primary-500"
          />
          <ChevronUpIcon
            v-else-if="isOpen"
            class="w-4"
          />
          <ChevronDownIcon
            v-else
            class="w-4"
          />
        </div>
      </div>
    </template>
    <template #menu="{ closeMenu }">
      <LpCard
        rounded-size="big"
        class="px-4 py-5 rounded lg:px-4 lg:py-5"
      >
        <LpInput
          v-model="accountFilter"
          name="search"
          :placeholder="$t('Search account')"
          @keyup.esc="accountFilter = ''"
        >
          <template #icon>
            <MagnifierIcon
              class="w-4 h-4"
            />
          </template>
        </LpInput>
        <ul class="overflow-y-scroll p-0 mt-4 space-y-2 max-h-96">
          <li
            v-for="option in accounts"
            :key="option.id"
          >
            <LpMarkerBtn
              v-if="option.id !== currentAccount.id"
              class="capitalize"
              data-testid="SelectAccountBtn"
              @click.prevent="handleAccountSelected(option, closeMenu)"
            >
              {{ option.name }}
            </LpMarkerBtn>
          </li>
        </ul>
      </LpCard>
    </template>
  </LpDropdown>
  <Teleport to="body">
    <div
      v-if="isSwitchingAccount"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-white"
    >
      <LpLoader
        class="text-primary-500"
        ring-size="50"
      />
    </div>
  </Teleport>
</template>
